import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCoverageMatrix,
  type CoverageCell,
  type CoverageSubtype,
  STATUS_LABELS,
  STATUS_COLORS,
} from './workflowDefinitionService';
import { PracticeAreaConfigService, type PracticeArea } from '../../../../../services/practiceAreaConfigService';

// ─── Props ───

interface Props {
  onOpenDefinition?: (definitionId: string) => void;
  onCreateDefinition?: (stateCode: string, practiceArea: string) => void;
}

// ─── State Display Names ───

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
};

// ─── Citation Health Helpers ───

function getHealthPct(s: CoverageSubtype): number {
  if (s.citation_health.total_tasks === 0) return 0;
  return Math.round((s.citation_health.cited_tasks / s.citation_health.total_tasks) * 100);
}

function getHealthDot(s: CoverageSubtype): string {
  const h = s.citation_health;
  if (h.total_tasks === 0) return 'bg-gray-300 dark:bg-gray-600';
  if (h.mandated_tasks > 0 && h.mandated_cited < h.mandated_tasks) return 'bg-rose-500';
  if (h.cited_tasks === h.total_tasks) return 'bg-emerald-500';
  if (getHealthPct(s) >= 80) return 'bg-amber-500';
  return 'bg-rose-500';
}

function getCellBg(cell: CoverageCell): string {
  if (cell.total_subtypes === 0) return 'bg-gray-50 dark:bg-gray-800/40';
  if (cell.status === 'complete') return 'bg-emerald-50 dark:bg-emerald-900/20';
  if (cell.status === 'partial') return 'bg-amber-50 dark:bg-amber-900/20';
  return 'bg-rose-50 dark:bg-rose-900/20';
}

function getCellBorder(cell: CoverageCell): string {
  if (cell.total_subtypes === 0) return 'border-gray-200 dark:border-gray-700';
  if (cell.status === 'complete') return 'border-emerald-200 dark:border-emerald-800';
  if (cell.status === 'partial') return 'border-amber-200 dark:border-amber-800';
  return 'border-rose-200 dark:border-rose-800';
}

function formatPA(name: string, displayMap: Map<string, string>): string {
  return displayMap.get(name) || name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Component ───

// ─── Main Component ───

export const WorkflowCoverageDashboard: React.FC<Props> = ({ onOpenDefinition, onCreateDefinition }) => {
  const [cells, setCells] = useState<CoverageCell[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Firm-scoped data
  const [firmStates, setFirmStates] = useState<string[]>([]);
  const [practiceAreas, setPracticeAreas] = useState<PracticeArea[]>([]);
  const paDisplayMap = useRef(new Map<string, string>());

  // Drill-down
  const [expandedCell, setExpandedCell] = useState<string | null>(null); // "CA|family_law"

  // Load firm data
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const paService = PracticeAreaConfigService.getInstance();
        const [available, firmSettings] = await Promise.all([
          paService.getAvailablePracticeAreas(),
          paService.getFirmSettings().catch(() => null),
        ]);
        if (!mounted) return;

        // Build display name lookup from the full catalog
        const map = new Map<string, string>();
        for (const pa of available) {
          map.set(pa.name, pa.displayName || pa.name);
        }
        paDisplayMap.current = map;

        // Filter to only practice areas the firm has selected
        const firmSelectedPAs = firmSettings?.practiceAreas || [];
        const firmPASet = new Set(firmSelectedPAs.map((p) => p.toLowerCase().replace(/\s+/g, '_')));
        const firmPAs = firmSelectedPAs.length > 0
          ? available.filter((a) => a.isActive && (firmPASet.has(a.name) || firmPASet.has(a.displayName.toLowerCase().replace(/\s+/g, '_'))))
          : available.filter((a) => a.isActive);
        setPracticeAreas(firmPAs);

        const states = firmSettings?.licensedStates?.length ? firmSettings.licensedStates : ['CA'];
        setFirmStates(states);
      } catch {
        if (mounted) setFirmStates(['CA']);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  // Load coverage data once we have firm data
  useEffect(() => {
    if (firmStates.length === 0 || practiceAreas.length === 0) return;
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const paNames = practiceAreas.map((p) => p.name);
        const data = await getCoverageMatrix(firmStates, paNames);
        if (!mounted) return;
        setCells(data);
      } catch (err) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load coverage data');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [firmStates, practiceAreas]);

  const getCell = useCallback((state: string, pa: string): CoverageCell | undefined => {
    return cells.find((c) => c.state_code === state && c.practice_area === pa);
  }, [cells]);

  const cellKey = (state: string, pa: string) => `${state}|${pa}`;

  const toggleExpand = (state: string, pa: string) => {
    const key = cellKey(state, pa);
    setExpandedCell((prev) => (prev === key ? null : key));
  };

  // ─── Aggregated stats ───
  const totalDefs = cells.reduce((sum, c) => sum + c.total_subtypes, 0);
  const publishedDefs = cells.reduce((sum, c) => sum + c.published_count, 0);
  const emptyCells = cells.filter((c) => c.total_subtypes === 0).length;
  const totalCells = firmStates.length * practiceAreas.length;

  return (
    <div className="space-y-5">
      {/* ─── Summary Stats ─── */}
      {!loading && cells.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{firmStates.length}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Licensed States</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{totalDefs}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Total Definitions</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{publishedDefs}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Published</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
            <div className="text-2xl font-bold text-gray-400 dark:text-gray-500">{emptyCells}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {emptyCells === 0 ? 'No Gaps' : `Gap${emptyCells !== 1 ? 's' : ''}`}
              <span className="text-gray-300 dark:text-gray-600"> / {totalCells}</span>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Loading coverage data...</span>
          </div>
        </div>
      )}

      {/* ─── Coverage Grid ─── */}
      {!loading && firmStates.length > 0 && practiceAreas.length > 0 && (
        <div className="overflow-x-auto -mx-1 px-1">
          <table className="min-w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 rounded-tl-lg">
                  State
                </th>
                {practiceAreas.map((pa) => (
                  <th
                    key={pa.name}
                    className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 whitespace-nowrap last:rounded-tr-lg"
                  >
                    {formatPA(pa.name, paDisplayMap.current)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {firmStates.map((state, stateIdx) => (
                <React.Fragment key={state}>
                  <tr className="group/row">
                    {/* State label */}
                    <td className={`sticky left-0 z-10 px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 bg-white dark:bg-gray-800 ${stateIdx === firmStates.length - 1 ? 'rounded-bl-lg' : ''}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{state}</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:inline">{STATE_NAMES[state] || ''}</span>
                      </div>
                    </td>

                    {/* Cells */}
                    {practiceAreas.map((pa, paIdx) => {
                      const cell = getCell(state, pa.name);
                      const key = cellKey(state, pa.name);
                      const isExpanded = expandedCell === key;
                      const isEmpty = !cell || cell.total_subtypes === 0;
                      const isLast = stateIdx === firmStates.length - 1 && paIdx === practiceAreas.length - 1;

                      return (
                        <td
                          key={key}
                          className={`px-2 py-2 border-b border-gray-100 dark:border-gray-700/50 transition-colors ${isLast ? 'rounded-br-lg' : ''}`}
                        >
                          <button
                            onClick={() => isEmpty && onCreateDefinition ? onCreateDefinition(state, pa.name) : toggleExpand(state, pa.name)}
                            className={`w-full min-w-[80px] rounded-lg border px-3 py-2.5 text-center transition-all duration-200 ${
                              isExpanded
                                ? 'ring-2 ring-lex-accent/30 shadow-md scale-[1.02]'
                                : 'hover:shadow-sm hover:scale-[1.01]'
                            } ${cell ? getCellBg(cell) : 'bg-gray-50 dark:bg-gray-800/40'} ${cell ? getCellBorder(cell) : 'border-gray-200 dark:border-gray-700 border-dashed'}`}
                            title={
                              isEmpty
                                ? `No definitions — ${onCreateDefinition ? 'click to create' : 'add one in the Statutory Library'}`
                                : `${cell!.published_count}/${cell!.total_subtypes} published — click to expand`
                            }
                          >
                            {isEmpty ? (
                              <div className="flex flex-col items-center gap-0.5">
                                {onCreateDefinition ? (
                                  <>
                                    <svg className="w-4 h-4 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                    </svg>
                                    <span className="text-[10px] text-gray-400 dark:text-gray-500">Create</span>
                                  </>
                                ) : (
                                  <span className="text-xs text-gray-300 dark:text-gray-600">--</span>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-1">
                                <div className="flex items-center gap-1.5">
                                  <span className={`text-sm font-bold ${
                                    cell!.status === 'complete'
                                      ? 'text-emerald-700 dark:text-emerald-400'
                                      : cell!.status === 'partial'
                                        ? 'text-amber-700 dark:text-amber-400'
                                        : 'text-rose-700 dark:text-rose-400'
                                  }`}>
                                    {cell!.published_count}/{cell!.total_subtypes}
                                  </span>
                                </div>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                  {cell!.status === 'complete' ? 'published' : cell!.published_count > 0 ? 'partial' : 'unpublished'}
                                </span>
                              </div>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>

                  {/* ─── Expanded Drill-Down Row ─── */}
                  {practiceAreas.some((pa) => expandedCell === cellKey(state, pa.name)) && (
                    <tr>
                      <td colSpan={practiceAreas.length + 1} className="px-0 py-0 border-b border-gray-100 dark:border-gray-700/50">
                        {(() => {
                          const expandedPA = practiceAreas.find((pa) => expandedCell === cellKey(state, pa.name));
                          if (!expandedPA) return null;
                          const cell = getCell(state, expandedPA.name);
                          if (!cell || cell.total_subtypes === 0) return null;

                          return (
                            <div
                              className="mx-4 my-3 rounded-xl border border-lex-accent dark:border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft p-4 overflow-hidden"
                              style={{ animation: 'coverageDrillDown 250ms ease-out forwards' }}
                            >
                              {/* Header */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-lex-accent-soft dark:bg-lex-accent-soft text-lex-accent dark:text-lex-accent">
                                    {state}
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {formatPA(expandedPA.name, paDisplayMap.current)}
                                  </span>
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    {cell.total_subtypes} subtype{cell.total_subtypes !== 1 ? 's' : ''}
                                  </span>
                                </div>
                                <button
                                  onClick={() => setExpandedCell(null)}
                                  className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>

                              {/* Subtype Cards */}
                              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {cell.subtypes.map((st) => {
                                  const sc = STATUS_COLORS[st.status] || STATUS_COLORS.draft;
                                  const healthPct = getHealthPct(st);
                                  const dotColor = getHealthDot(st);

                                  return (
                                    <button
                                      key={st.id}
                                      onClick={() => onOpenDefinition?.(st.id)}
                                      className="text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:shadow-md hover:border-lex-accent dark:hover:border-lex-accent transition-all group/card"
                                    >
                                      <div className="flex items-start justify-between gap-2 mb-1.5">
                                        <h4 className="text-sm font-medium text-gray-900 dark:text-white group-hover/card:text-lex-accent dark:group-hover/card:text-lex-accent transition-colors line-clamp-1">
                                          {st.label}
                                        </h4>
                                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${sc.bg} ${sc.text}`}>
                                          <span className={`w-1 h-1 rounded-full ${sc.dot}`} />
                                          {STATUS_LABELS[st.status]}
                                        </span>
                                      </div>

                                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
                                        <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-[10px]">
                                          {st.case_subtype.replace(/_/g, ' ')}
                                        </span>
                                        <span>v{st.version}</span>
                                      </div>

                                      {/* Citation Health Bar */}
                                      {st.citation_health.total_tasks > 0 && (
                                        <div className="flex items-center gap-2">
                                          <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                            <div
                                              className={`h-full rounded-full transition-all duration-500 ${
                                                st.citation_health.cited_tasks === st.citation_health.total_tasks
                                                  ? 'bg-emerald-500'
                                                  : st.citation_health.mandated_tasks > 0 && st.citation_health.mandated_cited < st.citation_health.mandated_tasks
                                                    ? 'bg-rose-500'
                                                    : 'bg-amber-500'
                                              }`}
                                              style={{ width: `${healthPct}%` }}
                                            />
                                          </div>
                                          <span className="inline-flex items-center gap-1 text-[10px] font-medium whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                                            {st.citation_health.cited_tasks}/{st.citation_health.total_tasks}
                                          </span>
                                        </div>
                                      )}

                                      {st.citation_health.total_tasks === 0 && (
                                        <div className="text-[10px] text-gray-400 dark:text-gray-500 italic">No tasks defined</div>
                                      )}
                                    </button>
                                  );
                                })}

                                {/* Quick-create card within drill-down */}
                                {onCreateDefinition && (
                                  <button
                                    onClick={() => onCreateDefinition(state, expandedPA.name)}
                                    className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-gray-400 dark:text-gray-500 hover:border-lex-accent dark:hover:border-lex-accent hover:text-lex-accent dark:hover:text-lex-accent hover:bg-lex-state-hover dark:hover:bg-lex-state-hover transition-all min-h-[80px]"
                                  >
                                    <svg className="w-5 h-5 mb-1" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                    </svg>
                                    <span className="text-xs font-medium">Add Subtype</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Legend ─── */}
      {!loading && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-500 dark:text-gray-400 pt-1">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-emerald-200 dark:bg-emerald-800 border border-emerald-300 dark:border-emerald-700" />
            All published
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-amber-200 dark:bg-amber-800 border border-amber-300 dark:border-amber-700" />
            Partial coverage
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-rose-200 dark:bg-rose-800 border border-rose-300 dark:border-rose-700" />
            None published
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded border-2 border-dashed border-gray-300 dark:border-gray-600" />
            No definitions
          </div>
          <div className="ml-auto flex items-center gap-3 border-l border-gray-200 dark:border-gray-700 pl-4">
            <span className="font-medium text-gray-600 dark:text-gray-300">Citation Health:</span>
            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> 100%</div>
            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> &ge;80%</div>
            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> Gaps</div>
          </div>
        </div>
      )}

      {/* ─── Empty state ─── */}
      {!loading && firmStates.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No licensed states configured</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Set your firm's licensed states in Firm Information to see coverage here.
          </p>
        </div>
      )}

      {/* ─── Injected animation ─── */}
      <style>{`
        @keyframes coverageDrillDown {
          from { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; margin-top: 0; margin-bottom: 0; }
          to   { opacity: 1; max-height: 600px; }
        }
      `}</style>
    </div>
  );
};
